const { translateString } = window.BAO.Utils

class TagalysSearch extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()
  }

  setupListeners () {
    super.setupListeners()

    if (!window?.onTagalysReady) return

    this._initialized = false

    this.currentPage = 0
    this.totalPages = 0
    this.itemsPerPage = 48

    this.isLoadingPage = false
    this.isRestoringFromUrl = false
    this.targetPageFromUrl = 1

    this.totalResultsCount = 0
    this.renderedResultsCount = 0

    this.handleInitialise = this.handleInitialise.bind(this)
    this.handleLoadMoreClick = this.handleLoadMoreClick.bind(this)
    this.updateLoadMoreButton = this.updateLoadMoreButton.bind(this)
    this.updatePaginationMeta = this.updatePaginationMeta.bind(this)
    this.renderProductsHtml = this.renderProductsHtml.bind(this)
    this.buildHeader = this.buildHeader.bind(this)
    this.handleFilterChange = this.handleFilterChange.bind(this)
    this.updateSelectedFilters = this.updateSelectedFilters.bind(this)
    this.buildSortSelect = this.buildSortSelect.bind(this)
    this.handleSortChange = this.handleSortChange.bind(this)
    this.handleActiveTagClick = this.handleActiveTagClick.bind(this)
    this.buildActiveFiltersFromApplied =
      this.buildActiveFiltersFromApplied.bind(this)

    this.listeners.add(
      this.els['load-more']?.element,
      'click',
      this.handleLoadMoreClick,
    )

    this.listeners.add(document, 'click', this.handleGlobalClick)

    window.onTagalysReady(this.handleInitialise)

    this.els.filters.elements.forEach((filtersEl) => {
      this.listeners.add(filtersEl, 'change', this.handleFilterChange)
    })
  }

  handleInitialise () {
    if (this._initialized) return
    this._initialized = true

    const searchQuery = this.getSearchQueryFromUrl()
    const paginationContainer = this.els?.['pagination-load-more']?.element
    const productsContainer = this.els.products.element

    if (!searchQuery) {
      if (productsContainer) productsContainer.innerHTML = ''
      return
    }

    this.targetPageFromUrl = this.getPageFromUrl()
    this.isRestoringFromUrl = this.targetPageFromUrl > 1

    // eslint-disable-next-line no-undef
    window.searchResultsInstance = Tagalys.APIConnector.SearchResults.new({
      params: { query: searchQuery, perPage: this.itemsPerPage },
      onSuccess: async (response, helpers) => {
        const redirectFromHelpers =
          typeof helpers.getRedirectURL === 'function'
            ? helpers.getRedirectURL()
            : null

        const redirectFromMetadata = response?.metadata?.redirect_to_url
        const redirectUrl = redirectFromHelpers || redirectFromMetadata

        if (redirectUrl) {
          try {
            const absoluteRedirect = new URL(
              redirectUrl,
              window.location.origin,
            ).toString()
            const currentUrl =
              window.location.origin +
              window.location.pathname +
              window.location.search

            if (absoluteRedirect !== currentUrl) {
              window.location.href = absoluteRedirect
              return
            }
          } catch (error) {
            console.error(
              '[Tagalys] invalid redirect URL:',
              redirectUrl,
              error,
            )
          }
        }

        this.buildFilters(helpers)
        this.buildActiveFiltersFromApplied(helpers)

        const page = response?.page ?? 1
        const totalPages = response?.total_pages ?? 1
        const total = response?.total_count ?? 0
        const products = response?.products ?? []

        this.currentPage = page
        this.totalPages = totalPages
        this.totalResultsCount = total

        this.updateSelectedFilters(helpers)
        this.buildSortSelect(helpers)

        const clampedTarget = Math.min(
          Math.max(1, this.targetPageFromUrl),
          this.totalPages,
        )

        if (this.currentPage === 1) this.buildHeader(response, helpers)

        const shouldAppend = this.currentPage > 1
        const newCount = await this.renderProductsHtml(products, shouldAppend)

        if (paginationContainer) {
          paginationContainer.hidden = this.totalResultsCount === 0
        }

        this.renderedResultsCount = shouldAppend
          ? this.renderedResultsCount + newCount
          : newCount
        this.updatePaginationMeta()

        this.restoreFocus()

        if (this.isRestoringFromUrl) {
          if (this.currentPage < clampedTarget) {
            window.searchResultsInstance.helpers.goToNextPage()
            return
          }
          this.isRestoringFromUrl = false
          this.setPageInUrl(this.currentPage, { replace: true })
        } else {
          this.setPageInUrl(this.currentPage, { replace: true })
        }

        this.isLoadingPage = false
        this.updateLoadMoreButton()
      },
      onFailure: (response, helpers) => {
        console.error('[Tagalys] onFailure', { response, helpers })

        this.isLoadingPage = false
        this.isRestoringFromUrl = false
        this.updateLoadMoreButton()
      },
    })

    window.searchResultsInstance.helpers.call()
  }

  handleLoadMoreClick () {
    if (this.isLoadingPage) return
    if (this.currentPage >= this.totalPages) return

    this.isLoadingPage = true
    this.updateLoadMoreButton()

    window.searchResultsInstance.helpers.goToNextPage()
  }

  handleFilterChange (event) {
    if (document.activeElement?.id) {
      this.dataset.restoreFocusId = document.activeElement.id
    }

    const input = event.target

    if (!input) return
    if (input.type !== 'checkbox') return

    const groupId = input.getAttribute('data-filter-id')
    const value = input.getAttribute('data-filter-value')

    if (!groupId) return

    const helpers = window.searchResultsInstance.helpers
    if (!helpers) return

    input.disabled = true
    this.isLoadingPage = true
    this.updateLoadMoreButton()

    try {
      if (input.checked) {
        helpers.applyFilter(groupId, value)
        helpers.call({ page: 1 })
      } else if (!input.checked) {
        helpers.clearFilter(groupId, value)
        helpers.call({ page: 1 })
      }

      this.currentPage = 0
      this.totalPages = 0
      this.renderedResultsCount = 0
      this.isRestoringFromUrl = false
      this.targetPageFromUrl = 1
    } catch (event) {
      console.error('[Tagalys] filter toggle failed', event)
    } finally {
      input.disabled = false
    }
  }

  handleSortChange (event) {
    const target = event.dataset.value
    const select = document.querySelector('select#SortBy')
    if (!select) return

    select.value = target
    const sortId = select.value
    const helpers = window.searchResultsInstance.helpers
    if (!helpers || !sortId) return

    select.disabled = true
    this.isLoadingPage = true
    this.updateLoadMoreButton()

    try {
      if (helpers.applySortOption) {
        helpers.applySortOption(sortId)
        helpers.call({ page: 1 })
      } else {
        const params = {
          ...(window.searchResultsInstance.params || {}),
          sort: sortId,
          page: 1,
        }
        window.searchResultsInstance.params = params
        helpers.call(params)
      }
      this.currentPage = 0
      this.totalPages = 0
      this.renderedResultsCount = 0
      this.isRestoringFromUrl = false
      this.targetPageFromUrl = 1
    } finally {
      select.disabled = false
    }

    event
      .closest('[data-filter-accordions-el="item"]')
      .setAttribute('aria-expanded', true)
  }

  updateLoadMoreButton () {
    const loadMoreButton = this.els?.['load-more']?.element
    if (!loadMoreButton) return

    const hasMorePages = this.currentPage < this.totalPages

    loadMoreButton.hidden = !hasMorePages
    loadMoreButton.disabled =
      this.isLoadingPage || this.isRestoringFromUrl || !hasMorePages

    const labelLoading = this.translations?.search?.pagination?.loading
    const labelMore = this.translations?.search?.pagination?.loadMore

    loadMoreButton.firstChild.innerText =
      this.isLoadingPage || this.isRestoringFromUrl ? labelLoading : labelMore
    if (this.isLoadingPage || this.isRestoringFromUrl) {
      loadMoreButton.setAttribute('aria-busy', 'true')
    } else {
      loadMoreButton.removeAttribute('aria-busy')
    }
  }

  updatePaginationMeta () {
    const paginationMetaElement = this.els?.['pagination-meta']?.element
    if (!paginationMetaElement) return

    const showingCount = this.renderedResultsCount
    const totalCount = this.totalResultsCount

    const template = this.translations?.search?.pagination?.resultsAmount

    paginationMetaElement.innerHTML = translateString(template, {
      count: String(showingCount),
      total: String(totalCount),
    })
  }

  buildHeader (response, helpers) {
    const headerTermElement = this.els?.['header-term']?.element
    if (!headerTermElement) return

    const resultsTotal = response?.total_count ?? 0
    const resultsTerm = response?.query ?? this.getSearchQueryFromUrl()
    const resultsOriginal = helpers.getOriginalQuery()

    if (resultsTotal > 0) {
      if (!helpers.isValidQuery()) {
        const template =
          this.translations?.search?.resultsWithCount?.wrongSpell

        headerTermElement.innerHTML = translateString(template, {
          count: String(resultsTotal),
          terms: String(resultsTerm),
          original: String(resultsOriginal),
        })
      } else {
        const template =
          resultsTotal === 1
            ? this.translations?.search?.resultsWithCount?.one
            : this.translations?.search?.resultsWithCount?.other

        headerTermElement.innerHTML = translateString(template, {
          count: String(resultsTotal),
          terms: String(resultsTerm),
        })
      }
    } else {
      headerTermElement.innerHTML = translateString(
        this.translations?.search?.noResults,
        {
          terms: String(resultsTerm),
        },
      )
    }
  }

  async updateView (currentView, newView) {
    const listClass = `pg-Search`

    await window.BAO.Cart.updateCartAttributes({
      current_style: newView,
    })

    this.classList.remove(`${listClass}-${currentView}Filters`)
    this.classList.add(`${listClass}-${newView}Filters`)

    this.setAttribute(`data-filter-view`, newView)
  }

  buildFilters (helpers) {
    const filterGroups = helpers.getFilters() || []

    const renderShell = () =>
      filterGroups
        .map((filterGroup, index) => {
          if (filterGroup.type === 'range') return ''

          const groupName = filterGroup.label
          const groupId = filterGroup.id

          return `
          <div
            class="clc-ProductGridFacets_Item"
            aria-expanded="false"
            data-filter-accordions-el="item"
            data-drawers-accordion="${groupName}"
            role="treeitem"
          >
            <div class="clc-ProductGridFacet">
              <button
                type="button"
                class="clc-ProductGridFacet_Summary fz-20_110"
                data-filter-accordions-el="trigger"
              >
                <div class="clc-ProductGridFacet_FilterLabel">
                  ${groupName}

                  <span
                    class="clc-ProductGridFacet_Count"
                    data-tagalys-search-el="facets-selected"
                    data-name="${groupId}"
                    data-index="CollectionFacets-${index}"
                  ></span>
                </div>

                <div class="clc-ProductGridFacet_SummaryIcon">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7.02292 5.87552L14.1246 5.87542M14.1246 5.87542L14.1246 12.8761M14.1246 5.87542L5.875 14.125"
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    ></path>
                  </svg>
                </div>
              </button>

              <div
                class="clc-ProductGridFacet_Display"
                data-collection-filters-el="facet"
                data-filter-group="${groupId}"
                data-index="CollectionFacets-${index}"
                role="treeitem"
              >
                <div class="clc-ProductGridFacet_Overflow">
                  <div
                    class="clc-ProductGridFacet_Body"
                    data-filter-accordions-el="content"
                  >
                  </div>
                </div>
              </div>
            </div>
          </div>
        `
        })
        .join('')

    this.els?.filters?.elements.forEach((containerEl) => {
      if (!containerEl.dataset.filtersInitialized) {
        containerEl.innerHTML = renderShell()
        containerEl.dataset.filtersInitialized = 'true'
      }
    })

    this.updateFilterContents(filterGroups)
  }

  updateFilterContents (filterGroups) {
    const renderItems = (filterGroup) => {
      const groupName = filterGroup.label
      const groupId = filterGroup.id

      const itemsHtml = (filterGroup.items || [])
        .map((filterItem, index) => {
          const id = filterItem.id
          const name = filterItem.label
          const count = filterItem.count
          const selected = !!filterItem.selected
          const inputName = groupId
          const inputValue = String(id)

          return `
          <li class="clc-ProductGridFacet_Item">
            <label
              class="clc-ProductGridFacet_Label fz-13_120"
              for="CollectionFacets-${groupName}-${index}"
              aria-disabled="false"
            >
              <input
                class="clc-ProductGridFacet_Checkbox"
                type="checkbox"
                name="${inputName}"
                value="${inputValue}"
                id="CollectionFacets-${groupName}-${index}"
                data-filter-id="${groupId}"
                data-filter-value="${inputValue}"
                ${selected ? 'checked' : ''}
              >

              <div class="clc-ProductGridFacet_CheckboxIcon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 7 6"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M6.1.5 1.924 4.7.5 3.268"
                  ></path>
                </svg>
              </div>

              <div class="clc-ProductGridFacet_Title">
                <span class="clc-ProductGridFacet_TitleText">
                  ${name}
                </span>

                <span class="clc-ProductGridFacet_TitleCount util-ScreenReaderOnly">
                  (${count})
                </span>
              </div>
            </label>
          </li>
        `
        })
        .join('')

      return `
      <fieldset class="frm-Form_Fieldset">
        <legend class="util-ScreenReaderOnly">
          Filter by ${groupName}
        </legend>

        <ul class="clc-ProductGridFacet_List">
          ${itemsHtml}
        </ul>
      </fieldset>
    `
    }

    this.els?.filters?.elements.forEach((containerEl) => {
      filterGroups.forEach((filterGroup) => {
        if (filterGroup.type === 'range') return

        const facetBody = containerEl.querySelector(
          `[data-filter-group="${filterGroup.id}"] .clc-ProductGridFacet_Body`,
        )

        if (!facetBody) return

        const newHtml = renderItems(filterGroup)

        if (facetBody.innerHTML !== newHtml) {
          facetBody.innerHTML = newHtml
        }
      })
    })
  }

  buildActiveFiltersFromApplied (helpers) {
    const containers = Array.from(
      this.querySelectorAll('[data-tagalys-search-el="active-facets"]'),
    )
    if (!containers.length) return

    const groups = helpers.getAppliedFilters() || []
    const active = []

    groups.forEach((group) => {
      const groupId = group?.id
      const groupName = group?.label ?? groupId
      const items = Array.isArray(group?.items) ? group.items : []

      items.forEach((item) => {
        if (item?.selected === undefined || item.selected) {
          const value = String(item?.id ?? item?.value ?? '')
          const label = item?.label ?? item?.label ?? value
          if (groupId && value) {
            active.push({ groupId, groupName, value, label })
          }
        }
      })
    })

    const itemsHtml = active
      .map(
        ({ groupId, groupName, value, label }) => `
    <div class="clc-ProductGridFacets_ActiveFacet">
      <button
        type="button"
        class="clc-ProductGridFacets_ActiveLink fz-13_120"
        data-active-tag
        data-filter-id="${groupId}"
        data-filter-value="${value}"
        aria-label="Remove ${groupName}: ${label}"
      >
        ${label}

        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M16.6663 3.3335L3.33301 16.6668M16.6663 16.6668L3.33301 3.3335" stroke="currentColor" stroke-linecap="round"></path>
        </svg>
      </button>
    </div>
  `,
      )
      .join('')

    const clearAllHtml = active.length
      ? `
        <div class="clc-ProductGridFacets_Clear">
          <button
            type="button"
            class="clc-ProductGridFacets_ClearLink fz-13_120"
            data-active-tag-clear-all
          >
            ${this.translations?.search?.filters?.clearAll}
          </button>
        </div>
      `
      : ''

    const html = active.length ? itemsHtml + clearAllHtml : ''
    containers.forEach((containerEl) => {
      containerEl.innerHTML = html
    })
  }

  updateSelectedFilters (helpers) {
    const selectedFacets = this.els['facets-selected']?.elements
    let totalSelected = 0

    if (selectedFacets) {
      if (helpers.getAppliedFilters().length > 0) {
        const appliedFilters = helpers.getAppliedFilters()

        selectedFacets.forEach((facetEl) => {
          const matchingFilter = appliedFilters.find(
            (filter) => filter.id === facetEl.dataset.name,
          )

          if (matchingFilter) {
            facetEl.innerText = `(${matchingFilter.items.length})`
          } else {
            facetEl.innerText = ''
          }
        })

        appliedFilters.forEach((filter) => {
          totalSelected += filter.items.length
        })

        this.els['filter-count'].elements.forEach((countEl) => {
          countEl.innerText = ` (${totalSelected})`
        })
      } else {
        selectedFacets.forEach((facetEl) => {
          facetEl.innerText = ''
        })

        this.els['filter-count'].elements.forEach((countEl) => {
          countEl.innerText = ''
        })
      }
    }
  }

  handleActiveTagClick (event) {
    const btn = event.target?.closest?.('[data-active-tag]')
    if (!btn) return

    const helpers = window.searchResultsInstance?.helpers
    if (!helpers) return

    this.isLoadingPage = true
    this.updateLoadMoreButton()

    try {
      if (btn.hasAttribute('data-active-tag')) {
        const groupId = btn.getAttribute('data-filter-id')
        const value = btn.getAttribute('data-filter-value')

        if (!groupId) return

        helpers.clearFilter(groupId, value)
        helpers.call({ page: 1 })
      } else if (btn.hasAttribute('data-active-tag-clear-all')) {
        helpers.clearAllFilters()
        helpers.call({ page: 1 })
      }

      this.currentPage = 0
      this.totalPages = 0
      this.renderedResultsCount = 0
      this.isRestoringFromUrl = false
      this.targetPageFromUrl = 1
    } catch (event) {
      console.error('[Tagalys] active tag action failed', event)
    }
  }

  handleDrawerClearClick () {
    const helpers = window.searchResultsInstance?.helpers
    if (!helpers) return

    this.isLoadingPage = true
    this.updateLoadMoreButton()

    try {
      if (helpers.clearAllFilters()) {
        helpers.clearAllFilters()
        helpers.call({ page: 1 })
      } else {
        helpers.call({ filters: {}, page: 1 })
      }
      this.currentPage = 0
      this.totalPages = 0
      this.renderedResultsCount = 0
      this.isRestoringFromUrl = false
      this.targetPageFromUrl = 1
    } catch (event) {
      console.error('[Tagalys] clear-all failed', event)
    }
  }

  handleGlobalClick = (event) => {
    const target = event.target

    const resetBtn = target.closest('[data-active-tag-clear-all]')
    if (resetBtn) {
      event.preventDefault()
      this.handleDrawerClearClick()
      return
    }

    const tagBtn = target.closest('[data-active-tag]')
    if (tagBtn && tagBtn.closest('[data-tagalys-search-el="active-facets"]')) {
      event.preventDefault()
      this.handleActiveTagClick(event)
    }

    const sortBtn = target.closest('[data-facet-sort-el="button"]')
    if (sortBtn) {
      this.handleSortChange(sortBtn)
    }
  }

  buildSortSelect (helpers) {
    const container = this.els.filters.element
    if (!container) return

    let sortWrapper = container.querySelector('[data-sort-container]')

    if (!sortWrapper) {
      sortWrapper = document.createElement('div')

      sortWrapper.setAttribute('data-sort-container', '')

      sortWrapper.innerHTML = `
      <div
        class="clc-ProductGridFacets_Item"
        aria-expanded="false"
        data-filter-accordions-el="item"
        data-drawers-accordion="sort"
        role="treeitem"
      >
        <div
          class="clc-ProductGridFacet"
          data-tagalys-search-el="sorting"
        >
          <button
            type="button"
            class="clc-ProductGridFacet_Summary fz-20_110"
            data-filter-accordions-el="trigger"
            data-facet-sort-external-el="accordion"
          >
            <span class="clc-ProductGridFacet_FilterLabel">
              ${this.translations.search.filters.sortBy}
            </span>

            <div class="clc-ProductGridFacet_SummaryIcon">
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.02292 5.87552L14.1246 5.87542M14.1246 5.87542L14.1246 12.8761M14.1246 5.87542L5.875 14.125"
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                ></path>
              </svg>
            </div>
          </button>

          <div
            class="clc-ProductGridFacet_Display"
            role="treeitem"
          >
            <div class="clc-ProductGridFacet_Overflow">
              <div
                class="clc-ProductGridFacet_Body"
                data-filter-accordions-el="content"
              >
                <select
                  name="sort_by"
                  tabindex="-1"
                  class="util-ScreenReaderOnly"
                  id="SortBy"
                  aria-describedby="a11y-refresh-page-message"
                  data-tagalys-sort
                ></select>

                <ul
                  class="clc-ProductGridFacet_List"
                  data-sort-buttons
                ></ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    `

      container.appendChild(sortWrapper)
    }

    this.updateSortSelect(helpers)
  }

  updateSortSelect (helpers) {
    const container = this.els.filters.element
    if (!container) return

    const selectEl = container.querySelector('[data-tagalys-sort]')
    const buttonsEl = container.querySelector('[data-sort-buttons]')

    if (!selectEl || !buttonsEl) return

    const options = helpers.getSortOptions() || []
    const applied = helpers.getAppliedSortOption() || null

    const optionsHtml = options
      .map((option) => {
        const selected =
          option.selected || (applied && applied.id === option.id)

        return `
        <option
          value="${option.id}"
          ${selected ? 'selected' : ''}
        >
          ${option.label}
        </option>
      `
      })
      .join('')

    const buttonsHtml = options
      .map((option) => {
        const selected =
          option.selected || (applied && applied.id === option.id)

        return `
        <li class="clc-ProductGridFacet_Item">
          <button
            type="button"
            class="clc-ProductGridFacet_SortButton fz-13_120"
            data-facet-sort-el="button"
            data-value="${option.id}"
            data-name="${option.label}"
            ${selected ? 'aria-current="page"' : ''}
          >
            <div class="clc-ProductGridFacet_CheckboxIcon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 7 6"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M6.1.5 1.924 4.7.5 3.268"
                />
              </svg>
            </div>

            <div class="clc-ProductGridFacet_Title">
              <span class="clc-ProductGridFacet_TitleText">
                ${option.label}
              </span>
            </div>
          </button>
        </li>
      `
      })
      .join('')

    if (selectEl.innerHTML !== optionsHtml) {
      selectEl.innerHTML = optionsHtml
    }

    if (buttonsEl.innerHTML !== buttonsHtml) {
      buttonsEl.innerHTML = buttonsHtml
    }
  }

  async renderProductsHtml (productsArray, appendInsteadOfReplace) {
    const productsContainerElement = this.els?.products?.element
    if (!productsContainerElement) return 0

    const htmlForNewCards = await this.createProductCardsHtml(productsArray)
    const newCount = (productsArray || []).length

    if (appendInsteadOfReplace) {
      productsContainerElement.insertAdjacentHTML('beforeend', htmlForNewCards)
    } else {
      productsContainerElement.innerHTML = htmlForNewCards
    }

    return newCount
  }

  async createProductCardsHtml (productsArray) {
    const productCardHtmlList = await Promise.all(
      (productsArray || []).map(async (product) => {
        const productUrl = `${window.Shopify.routes.root}products/${product.handle}`
        try {
          const sectionHtml = await window.BAO.sectionRenderer.fetchSingle(
            'tagalys-product-card',
            productUrl,
          )
          const sectionDocument =
            window.BAO.sectionRenderer.convertToDOM(sectionHtml)
          const articleElement = sectionDocument.querySelector('article')

          return articleElement
            ? `<div class="prd-List_Item">${articleElement.outerHTML}</div>`
            : ''
        } catch (error) {
          this.handleError?.(error)
          return ''
        }
      }),
    )

    return productCardHtmlList.join('')
  }

  getSearchQueryFromUrl () {
    const params = new URLSearchParams(window.location.search)
    const raw = params.get('q') ?? params.get('query') ?? ''
    return raw.replace(/\+/g, ' ').trim()
  }

  getPageFromUrl () {
    const params = new URLSearchParams(window.location.search)
    const page = parseInt(params.get('page') || '1', 10)
    return Number.isFinite(page) && page > 0 ? page : 1
  }

  setPageInUrl (pageNumber, { replace = true } = {}) {
    const url = new URL(window.location.href)
    if (pageNumber === 1) url.searchParams.delete('page')
    else url.searchParams.set('page', String(pageNumber))

    const method = replace ? 'replaceState' : 'pushState'
    window.history[method](null, '', url.toString())
  }

  restoreFocus () {
    const restoreId = this.dataset.restoreFocusId
    if (!restoreId) return

    const el = this.querySelector(`#${CSS.escape(restoreId)}`)
    if (el) el.focus()

    delete this.dataset.restoreFocusId
  }

  get translations () {
    return window.theme?.strings ?? {}
  }
}

if (!customElements.get('tagalys-search')) {
  customElements.define('tagalys-search', TagalysSearch)
}
